import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AuthRequest,
    changeInitialPasswordRequest,
    LoginResponse,
    NotActivatedUserStatusResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
} from '@routes/auth/___shared___/auth.api.ts';
import { activateUser, resetPassword, userStatus } from '@routes/auth/___shared___/auth.api.ts';
import { persistToken, readToken } from '@store/services/localStorage.service.ts';
import { extractError } from '@utils/slice.ts';

export interface AuthSlice {
    token: string | null;
    error: Record<string, any> | null;
    userStatus: string;
    email: string;
    message: string;
    userType: string;

    isCheckNotActivatedUserStatusPending: boolean;
    isSetUserNewPasswordFirstTimePending: boolean;
    isUpdatePasswordPending: boolean;
    isResetUserPasswordPending: boolean;
}

const initialState: AuthSlice = {
    error: null,
    token: readToken(),
    userStatus: '',
    email: '',
    message: '',
    userType: '',

    isCheckNotActivatedUserStatusPending: false,
    isSetUserNewPasswordFirstTimePending: false,
    isUpdatePasswordPending: false,
    isResetUserPasswordPending: false,
};

export const checkNotActivatedUserStatus = createAsyncThunk<
    NotActivatedUserStatusResponse,
    AuthRequest,
    { rejectValue: any }
>('auth/checkNotActivatedUserStatus', (checkStatusPayload, { rejectWithValue }) =>
    userStatus(checkStatusPayload)
        .then((res) => {
            return res;
        })
        .catch((err) => {
            return rejectWithValue(err);
        }),
);

export const setUserNewPasswordFirstTime = createAsyncThunk<
    LoginResponse,
    changeInitialPasswordRequest,
    { rejectValue: any }
>('auth/setUsersLongTermPassword', (userNewPasswordPayload, { rejectWithValue }) =>
    activateUser(userNewPasswordPayload)
        .then((res) => {
            persistToken(res.accessToken);
            return res;
        })
        .catch((err) => {
            return rejectWithValue(err);
        }),
);

export const setEmail = createAsyncThunk<string, string>('auth/setEmail', async (email) => {
    return email;
});

export const resetUserPassword = createAsyncThunk<ResetPasswordResponse, ResetPasswordRequest, { rejectValue: any }>(
    'auth/resetPassword',
    (resetPasswordPayload, { rejectWithValue }) =>
        resetPassword(resetPasswordPayload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const authRegisterSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUserType(state, action: PayloadAction<string>) {
            state.userType = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(checkNotActivatedUserStatus.pending, (state) => {
            state.error = null;
            state.isCheckNotActivatedUserStatusPending = true;
        });
        builder.addCase(checkNotActivatedUserStatus.fulfilled, (state, action) => {
            state.userStatus = action.payload.status;
            state.isCheckNotActivatedUserStatusPending = false;
        });
        builder.addCase(checkNotActivatedUserStatus.rejected, (state, action) => {
            state.error = extractError(action);
            state.isCheckNotActivatedUserStatusPending = false;
        });
        builder.addCase(setUserNewPasswordFirstTime.pending, (state) => {
            state.error = null;
            state.isSetUserNewPasswordFirstTimePending = true;
        });
        builder.addCase(setUserNewPasswordFirstTime.fulfilled, (state, action) => {
            state.token = action.payload.accessToken;
            state.isSetUserNewPasswordFirstTimePending = false;
        });
        builder.addCase(setUserNewPasswordFirstTime.rejected, (state, action) => {
            state.error = extractError(action);
            state.isSetUserNewPasswordFirstTimePending = false;
        });
        builder.addCase(setEmail.fulfilled, (state, action) => {
            state.email = action.payload;
        });
        builder.addCase(resetUserPassword.pending, (state) => {
            state.error = null;
            state.isResetUserPasswordPending = true;
        });
        builder.addCase(resetUserPassword.fulfilled, (state, action) => {
            state.message = action.payload.message;
            state.isResetUserPasswordPending = false;
        });
        builder.addCase(resetUserPassword.rejected, (state, action) => {
            state.error = extractError(action);
            state.isResetUserPasswordPending = false;
        });
    },
});

export const { setUserType } = authRegisterSlice.actions;

export default authRegisterSlice.reducer;
